import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2Off } from "lucide-react";

import { showError, showSuccess } from "@/lib/toast";
import {
  listQuotationItemCandidates,
  updateProjectItem,
  type ProjectItem,
  type QuotationItemCandidate,
} from "./api";

/**
 * 案件明細の側から見積明細を選んで結び付けるダイアログ。
 * **明細一覧の「見積」の列から開く**。
 *
 * 受注にするときの突き合わせは**図番が 1:1 で決まる行だけ**を結ぶ
 * （docs/urgent-project-flow.md）。同じ図番が複数ある行（分納で案件を分けて
 * いる場合など）と、受注のあとに案件へ足した行はここで手当てする。
 *
 * 候補に出すのは**まだどの案件明細にも使われていない見積明細**だけ。既に使われて
 * いる行を選べると、選んだ瞬間に別の案件明細から金額の根拠が外れる（図面の紐付け
 * と同じ絞り方。[[ItemDrawingLinkDialog]]）。
 */
export default function ItemQuotationLinkDialog({
  projectId,
  item,
  canWrite,
  onClose,
}: {
  projectId: number;
  /** null のとき閉じている。 */
  item: ProjectItem | null;
  canWrite: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const candidates = useQuery({
    queryKey: ["quotation-item-candidates", projectId, item?.project_item_id],
    queryFn: () =>
      listQuotationItemCandidates(projectId, item?.project_item_id as number),
    enabled: item != null,
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["project", projectId] }),
      queryClient.invalidateQueries({ queryKey: ["project-items", projectId] }),
      queryClient.invalidateQueries({ queryKey: ["projects"] }),
    ]);

  const link = useMutation({
    mutationFn: (candidate: QuotationItemCandidate | null) =>
      updateProjectItem(projectId, item?.project_item_id as number, {
        quotation_item_id: candidate ? candidate.quotation_item_id : null,
      }),
    onSuccess: async (_data, candidate) => {
      await invalidate();
      showSuccess(
        candidate
          ? `${item?.sequence} 行目を見積 ${candidate.quotation_number} の ${candidate.sequence} 行目に結び付けました。`
          : "見積との結び付けを外しました。",
      );
      onClose();
    },
    onError: (e: Error) => showError(e.message),
  });

  const rows = candidates.data?.items ?? [];
  const busy = link.isPending;

  return (
    <Modal
      isOpen={item != null}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>見積明細との結び付け</span>
          <span className="text-small font-normal text-default-500">
            {item?.sequence} 行目 {item?.drawing_number} {item?.item_name}
          </span>
        </ModalHeader>

        <ModalBody>
          {/* いま結び付いている相手は「外す」だけ出す。付け替えたいときは外して
              から選び直す（候補の絞り方と揃える）。 */}
          {item?.quotation_item_id != null && (
            <div className="flex items-center gap-3 rounded-md border border-brand-200 bg-brand-50 p-3">
              <p className="min-w-0 flex-1 text-sm">
                この行は既に見積明細と結び付いています。
                <span className="block text-tiny text-default-500">
                  外すと、この行の図番・品名・材質・表面処理・数量をまた直せるようになります。
                </span>
              </p>
              <Button
                size="sm"
                radius="full"
                variant="flat"
                color="danger"
                startContent={<Link2Off size={16} />}
                isDisabled={!canWrite || busy}
                data-testid="unlink-quotation-item"
                onPress={() => link.mutate(null)}
              >
                結び付けを外す
              </Button>
            </div>
          )}

          {candidates.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner label="読み込み中..." />
            </div>
          ) : rows.length === 0 ? (
            <p className="py-2 text-sm text-default-500">
              選べる見積明細がありません。この案件の見積がまだ無いか、見積の明細がすべて別の行で使われています。
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-default-100">
              {rows.map((candidate) => (
                <li
                  key={candidate.quotation_item_id}
                  className="flex items-center gap-3 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold">{candidate.drawing_number}</p>
                    <p className="truncate text-tiny text-default-400">
                      {candidate.quotation_number} の {candidate.sequence} 行目 /{" "}
                      {candidate.item_name} / 数量 {candidate.quantity}
                    </p>
                  </div>
                  {/* 図番が一致する行は、まず間違いなくこれ。並び順だけでは
                      「上にあるから」なのか「一致したから」なのか分からない。 */}
                  {candidate.drawing_number_matches && (
                    <Chip size="sm" variant="flat" color="success">
                      図番一致
                    </Chip>
                  )}
                  <Button
                    size="sm"
                    radius="full"
                    color="primary"
                    variant={candidate.drawing_number_matches ? "solid" : "bordered"}
                    // 同じ文言のボタンが並ぶので、読み上げにはどの行かを乗せる。
                    aria-label={`${candidate.quotation_number} の ${candidate.sequence} 行目に結び付ける`}
                    isDisabled={!canWrite || busy}
                    onPress={() => link.mutate(candidate)}
                  >
                    結び付ける
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </ModalBody>

        <ModalFooter>
          <Button radius="full" variant="light" data-testid="close" onPress={onClose}>
            閉じる
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
